/**
 * Périodes de tarif d'un type de consultation.
 *
 * Un type n'a plus un prix écrasé : chaque montant vaut entre deux jours civils du cabinet
 * (Europe/Luxembourg). Le tarif actuel est la période dont le début est le plus récent parmi
 * celles déjà commencées, et dont la fin est vide ou encore à venir. Une date de début future
 * est un tarif programmé : elle n'est pas le tarif actuel. `validTo` nul signifie « sans fin »,
 * pas forcément « en cours » — une future programmée a la fin vide, et la période d'aujourd'hui
 * s'arrête la veille. Les centimes sont des entiers : jamais une chaîne.
 */

const LUX_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Luxembourg',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Jour civil du cabinet, `YYYY-MM-DD`. */
export function luxembourgCivilDate(instant: Date): string {
  return LUX_DAY.format(instant);
}

/** Décale un jour civil de `days` jours. Le calcul est calendaire, sans heure. */
export function addCivilDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Euros déjà numériques → centimes entiers. Deux décimales, découpées sur le texte de
 * `toFixed` : le montant ne passe pas par une multiplication flottante.
 */
export function eurosToCentsNumber(euros: number): number {
  if (!Number.isFinite(euros)) {
    throw new Error('montant non fini');
  }
  const negative = euros < 0;
  const [whole, fraction] = Math.abs(euros).toFixed(2).split('.');
  const cents = Number(whole) * 100 + Number(fraction);
  return negative ? -cents : cents;
}

/** Une période de tarif. `validTo` nul : pas de date de fin. */
export interface ConsultationTypePricePeriod {
  readonly priceCents: number;
  readonly validFrom: string;
  readonly validTo: string | null;
}

function byStart(periods: readonly ConsultationTypePricePeriod[]): ConsultationTypePricePeriod[] {
  return [...periods].sort((a, b) => (a.validFrom < b.validFrom ? -1 : a.validFrom > b.validFrom ? 1 : 0));
}

/**
 * Période qui couvre `on` (`YYYY-MM-DD`). `null` s'il n'y en a aucune : on n'emprunte pas le
 * tarif d'une autre date. Deux périodes qui se chevaucheraient, la plus récente l'emporte.
 */
export function consultationTypePeriodOn(
  periods: readonly ConsultationTypePricePeriod[],
  on: string,
): ConsultationTypePricePeriod | null {
  const covering = periods.filter(
    (period) => period.validFrom <= on && (period.validTo === null || period.validTo >= on),
  );
  covering.sort((a, b) => (a.validFrom < b.validFrom ? 1 : a.validFrom > b.validFrom ? -1 : 0));
  return covering[0] ?? null;
}

/** Centimes du tarif qui couvre `on`, ou `null`. */
export function consultationTypePriceCentsOn(
  periods: readonly ConsultationTypePricePeriod[],
  on: string,
): number | null {
  return consultationTypePeriodOn(periods, on)?.priceCents ?? null;
}

/**
 * Périodes après avoir fixé `nextCents` à partir de `start`.
 *
 * `null` : rien à écrire. Le montant déjà porté par la période qui couvre `start` n'ouvre pas
 * une période de plus. Une date future laisse la période d'aujourd'hui se terminer la veille et
 * ouvre la nouvelle sans fin. Une date passée termine la période qui couvrait ce jour la veille,
 * et ne réécrit pas ce qui a commencé avant. Une date égale au début d'une période corrige son
 * montant. Les périodes ne se chevauchent pas ; une seule a une fin vide.
 */
export function planConsultationTypePriceChange(
  periods: readonly ConsultationTypePricePeriod[],
  nextCents: number | null,
  start: string,
): readonly ConsultationTypePricePeriod[] | null {
  const ordered = byStart(periods);
  const covering =
    ordered.find(
      (period) => period.validFrom <= start && (period.validTo === null || period.validTo >= start),
    ) ?? null;

  if (nextCents === null) {
    if (covering === null) return null;
    if (covering.validFrom === start) return ordered.filter((period) => period !== covering);
    return ordered.map((period) =>
      period === covering ? { ...period, validTo: addCivilDays(start, -1) } : period,
    );
  }

  if (covering !== null && covering.priceCents === nextCents) return null;

  if (covering !== null && covering.validFrom === start) {
    return ordered.map((period) =>
      period === covering ? { ...period, priceCents: nextCents } : period,
    );
  }

  const following = ordered.find((period) => period.validFrom > start);
  const validTo = covering
    ? covering.validTo
    : following
      ? addCivilDays(following.validFrom, -1)
      : null;
  const inserted: ConsultationTypePricePeriod = { priceCents: nextCents, validFrom: start, validTo };
  const kept =
    covering === null
      ? ordered
      : ordered.map((period) =>
          period === covering ? { ...period, validTo: addCivilDays(start, -1) } : period,
        );
  return byStart([...kept, inserted]);
}

/**
 * Centimes à poser sur une ligne au moment où on la compose.
 *
 * Une date de séance ne retombe jamais sur le tarif d'aujourd'hui. Sans date de séance, c'est le
 * tarif actuel — celui qui couvre le jour civil du cabinet. Si aucune période ne couvre le jour
 * mais que le type n'a qu'un seul tarif, commençant après ce jour, on le prend : il n'y a pas
 * d'autre montant possible. Plusieurs tarifs sans aucun en vigueur : `null`, on ne choisit pas.
 */
export function unitPriceCentsForConsultationType(
  type: { readonly price: number | null; readonly prices: readonly ConsultationTypePricePeriod[] },
  sessionDate: string | null,
): number | null {
  const on = sessionDate ?? luxembourgCivilDate(new Date());
  const quoted = consultationTypePriceCentsOn(type.prices, on);
  if (quoted !== null) return quoted;
  const [only] = type.prices;
  if (type.prices.length === 1 && only !== undefined && only.validFrom > on) {
    return only.priceCents;
  }
  if (sessionDate !== null || type.prices.length > 0) return null;
  if (type.price === null) return null;
  return eurosToCentsNumber(type.price);
}
